import { Appointment } from '@/types';

export interface PendingForm {
  app: Appointment;
  templateId: string;
}

/**
 * Given the customer's currently-pending questionnaire forms and the appointment
 * id carried by a magic link (`?mode=questionnaire&appointment=...`), return the
 * form that should be auto-opened, or `null` if none matches.
 *
 * Matching is case-insensitive to mirror how appointment ids are compared
 * throughout CustomerPortal (booking references are stored uppercase but a link
 * may carry any casing).
 */
export const findPendingFormForAppointment = (
  pendingForms: readonly PendingForm[],
  appointmentId: string | null | undefined
): PendingForm | null => {
  if (!appointmentId) return null;
  const target = appointmentId.trim().toLowerCase();
  if (!target) return null;
  return pendingForms.find((pending) => pending.app.id?.toLowerCase() === target) ?? null;
};

const idsMatch = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/**
 * Outcome of resolving a magic-link appointment id against the customer's loaded
 * data. Drives what the portal shows when someone opens a
 * `?mode=questionnaire&appointment=...` link:
 *
 * - `pending`  — a form is due; open it.
 * - `completed`— the required questionnaire(s) exist and are already submitted;
 *                reassure the customer instead of leaving a blank screen.
 * - `not-required` — the appointment needs no questionnaire.
 * - `not-found`— the appointment isn't in the customer's data (still loading, or
 *                the link belongs to a different account); stay silent and retry.
 */
export type MagicLinkStatus = 'pending' | 'completed' | 'not-required' | 'not-found';

/**
 * Classify what a magic link should do, given the target appointment id and the
 * customer's appointments (with per-appointment required/pending template
 * lookups). Kept pure so it can be unit-tested without rendering the portal.
 */
export const classifyMagicLinkTarget = ({
  appointmentId,
  appointments,
  getRequiredTemplateIds,
  getPendingTemplateIds
}: {
  appointmentId: string | null | undefined;
  appointments: readonly Appointment[];
  getRequiredTemplateIds: (app: Appointment) => string[];
  getPendingTemplateIds: (app: Appointment) => string[];
}): MagicLinkStatus => {
  if (!appointmentId || !appointmentId.trim()) return 'not-found';
  const app = appointments.find((candidate) => idsMatch(candidate.id, appointmentId));
  if (!app) return 'not-found';
  if (getPendingTemplateIds(app).length > 0) return 'pending';
  if (getRequiredTemplateIds(app).length === 0) return 'not-required';
  return 'completed';
};
